import type { UmbTreeItemModel, UmbTreeRootModel, UmbTreeStartNode } from '../../types.js';
import { UmbTreeViewElementBase } from '../tree-view-element-base.js';
import { css, customElement, html, nothing, repeat, state } from '@umbraco-cms/backoffice/external/lit';

@customElement('umb-classic-tree-view')
export class UmbClassicTreeViewElement extends UmbTreeViewElementBase {
	@state()
	private _rootItems: UmbTreeItemModel[] = [];

	@state()
	private _hasPreviousItems = false;

	@state()
	private _hasNextItems = false;

	@state()
	private _isLoadingPrevChildren = false;

	@state()
	private _isLoadingNextChildren = false;

	@state()
	private _hideTreeRoot = false;

	@state()
	private _hideTreeItemActions = false;

	@state()
	private _isMenu = false;

	@state()
	private _startNode?: UmbTreeStartNode;

	@state()
	private _startNodes: Array<UmbTreeStartNode> = [];

	protected override _gotTreeContext() {
		super._gotTreeContext();
		this.observe(this._treeContext?.rootItems, (rootItems) => (this._rootItems = rootItems ?? []), '_observeRootItems');
		this.observe(
			this._treeContext?.isLoadingPrevChildren,
			(value) => (this._isLoadingPrevChildren = value ?? false),
			'_observeIsLoadingPrevChildren',
		);
		this.observe(
			this._treeContext?.isLoadingNextChildren,
			(value) => (this._isLoadingNextChildren = value ?? false),
			'_observeIsLoadingNextChildren',
		);
		this.observe(
			this._treeContext?.targetPagination?.totalPrevItems,
			(value) => (this._hasPreviousItems = value ? value > 0 : false),
			'_observeTotalPrevItems',
		);
		this.observe(
			this._treeContext?.targetPagination?.totalNextItems,
			(value) => (this._hasNextItems = value ? value > 0 : false),
			'_observeTotalNextItems',
		);
		this.observe(
			this._treeContext?.hideTreeRoot,
			(value) => (this._hideTreeRoot = value ?? false),
			'_observeHideTreeRoot',
		);
		this.observe(
			this._treeContext?.hideTreeItemActions,
			(value) => (this._hideTreeItemActions = value ?? false),
			'_observeHideTreeItemActions',
		);
		this.observe(this._treeContext?.isMenu, (value) => (this._isMenu = value ?? false), '_observeIsMenu');
		this.observe(this._treeContext?.startNode, (value) => (this._startNode = value), '_observeStartNode');
		this.observe(this._treeContext?.startNodes, (value) => (this._startNodes = value ?? []), '_observeStartNodes');
	}

	// The start nodes replace the tree root as the top level, exactly as a single start node does.
	get #hasStartNode(): boolean {
		return this._startNode !== undefined || this._startNodes.length > 0;
	}

	#onLoadPrev(event: Event) {
		event.stopPropagation();
		this._treeContext?.loadPrevItems?.();
	}

	#onLoadNext(event: Event) {
		event.stopPropagation();
		this._treeContext?.loadMore?.();
	}

	override render() {
		if (this._isMenu) {
			return html`${this.#renderTreeRoot()} ${this.#renderRootItems()}`;
		}
		// When the tree root is hidden or we are drilled into a start node only the children are shown.
		// With no children there is nothing to frame, so render nothing and let the tree host present the empty state.
		if ((this._hideTreeRoot || this.#hasStartNode) && this._rootItems.length === 0) {
			return nothing;
		}
		return html` <uui-box> ${this.#renderTreeRoot()} ${this.#renderRootItems()} </uui-box> `;
	}

	#renderTreeRoot() {
		// When drilled into a start node, the root node is replaced by the drilled children via #renderRootItems.
		if (this._hideTreeRoot || this.#hasStartNode || this._treeRoot === undefined) return nothing;
		return html`
			<umb-tree-item
				.entityType=${(this._treeRoot as UmbTreeRootModel).entityType}
				.props=${{
					hideActions: this._hideTreeItemActions,
					item: this._treeRoot,
					isMenu: this._isMenu,
				}}></umb-tree-item>
		`;
	}

	#renderRootItems() {
		// Render when hideTreeRoot is true, OR when drilled into a start node (startNode replaces the root node).
		if (!this._hideTreeRoot && !this.#hasStartNode) return nothing;
		return html`
			${this.#renderLoadPrevButton()}
			${repeat(
				this._rootItems,
				(item) => `${item.entityType}:${item.unique}`,
				(item) => html`
					<umb-tree-item
						.entityType=${item.entityType}
						.props=${{
							hideActions: this._hideTreeItemActions,
							item,
							isMenu: this._isMenu,
						}}></umb-tree-item>
				`,
			)}
			${this.#renderLoadNextButton()}
		`;
	}

	#renderLoadPrevButton() {
		if (!this._hasPreviousItems) return nothing;
		return html`<umb-tree-load-prev-button
			@click=${this.#onLoadPrev}
			.loading=${this._isLoadingPrevChildren}></umb-tree-load-prev-button>`;
	}

	#renderLoadNextButton() {
		if (!this._hasNextItems) return nothing;
		return html`<umb-tree-load-more-button
			@click=${this.#onLoadNext}
			.loading=${this._isLoadingNextChildren}></umb-tree-load-more-button>`;
	}

	static override styles = css`
		:host {
			display: contents;
		}
	`;
}

export default UmbClassicTreeViewElement;

declare global {
	interface HTMLElementTagNameMap {
		'umb-classic-tree-view': UmbClassicTreeViewElement;
	}
}
